<template>
  <div
    v-if="hoverData"
    v-show="dataState === 'loaded' && isLeafBoxHoverData(hoverData) && (isHeaderShown || isSharpChartShown)"
    class="hover-box"
    :style="{
      left: `${left}px`,
      top: `${top}px`
    }"
    ref="hoverBox"
  >
    <div
      v-show="isHeaderShown"
      class="upper-container"
    >
      <span class="company">{{ hoverData.name }} {{ 'symbol' in hoverData ? `(${hoverData.symbol})` : '' }}</span>
      <span class="break"></span>
      <span
        v-if="isLeafBoxHoverData(hoverData)"
        class="value"
        :style="{
          color: textColor
        }"
      >{{ hoverData.calculatedValues.display }}</span>
    </div>
    <div
      v-show="isHeaderShown && isSharpChartShown"
      class="separator"
    ></div>
    <div
      v-show="isSharpChartShown"
      class="lower-container"
    >
      <img :src="lastLoadedHoverChartData.sharpChartLink">
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { toRefs, computed, ref, watch, reactive } from 'vue';
  import { clamp } from '@/ts/util/util';
  import { useBoundingClientRect } from '@/ts/composables/useBoundingClientRect';
  import { useMarketCarpetContext } from '@/ts/marketCarpetContext';
  import { type LeafBoxHoverData, type ParentBoxHoverData, isLeafBoxHoverData } from '@/ts/composables/useHoverBox';
  import ExclusiveDelayedCallbackHandler from '@/ts/util/ExclusiveDelayedCallbackHandler';

  const COLORS_DOWN_NEUTRAL_UP: [down: string, neutral: string, up: string] = ['#CC000E', '#000000', '#1E824C'];
  const HORIZONTAL_OFFSET = 100;

  const props = defineProps<{
    hoverData: ParentBoxHoverData | LeafBoxHoverData | null;
    carpetWidth: number;
    carpetHeight: number;
    leftOffset?: number;
    topOffset?: number;
  }>();

  const { isHeaderShown, isSharpChartShown, dataState } = useMarketCarpetContext();

  const { hoverData, carpetWidth, carpetHeight, topOffset } = toRefs(props);
  const hoverBox = ref<HTMLElement>();
  const lastLoadedHoverChartData = reactive({ symbol: '', sharpChartLink: '' });
  const sharpChartImageSetterDelayHandler = new ExclusiveDelayedCallbackHandler();
  const { width, height } = useBoundingClientRect(hoverBox, { windowResize: true, windowScroll: true });

  const top = computed(() => {
    if (topOffset.value === undefined)
      return null;

    const centerOfMousePosition = topOffset.value - height.value / 2;
    return clamp(0, centerOfMousePosition, carpetHeight.value - height.value);
  });

  const left = computed(() => {
    if (!hoverData.value)
      return null;

    const isBoundToRight = hoverData.value.mouseEvent.clientX + width.value + HORIZONTAL_OFFSET < carpetWidth.value;
    const xOffset = isBoundToRight ? HORIZONTAL_OFFSET : -(width.value + HORIZONTAL_OFFSET);
    return clamp(0, hoverData.value.mouseEvent.clientX + xOffset, carpetWidth.value);
  });

  const textColor = computed(() => {
    if (!isLeafBoxHoverData(hoverData.value))
      return undefined;

    if (hoverData.value.calculatedValues.value < 0)
      return COLORS_DOWN_NEUTRAL_UP[0];
    else if (hoverData.value.calculatedValues.value === 0)
      return COLORS_DOWN_NEUTRAL_UP[1];
    else
      return COLORS_DOWN_NEUTRAL_UP[2];
  });

  watch(hoverData, () => { // can't use computed since in order for a smooth image transition, we can't have have the link be null
    if (!isLeafBoxHoverData(hoverData.value))
      return;

    const symbol = hoverData.value.symbol.toUpperCase();
    const isInitialImageLoad = !lastLoadedHoverChartData.sharpChartLink;
    sharpChartImageSetterDelayHandler.setTimeout(() => {
      if (lastLoadedHoverChartData.symbol === symbol)
        return;
      lastLoadedHoverChartData.symbol = symbol;
      lastLoadedHoverChartData.sharpChartLink = `/c-sc/sc?chart=${encodeURIComponent(symbol)},uu[290,168]dalayaci[dd]&r=${Date.now()}`;
    }, isInitialImageLoad ? 0 : 150);
  });
</script>

<style scoped>
  .hover-box {
    box-sizing: border-box;
    position: absolute;
    min-width: 290px;
    font-size: 15px;
    background-color: white;
    z-index: 100;
    border: 3px solid #000000;
  }

  .hover-box .upper-container {
    display: flex;
    flex-wrap: wrap;
    font-weight: 800;
    padding: 5px;
    flex-direction: column;
  }

  .upper-container .value {
    font-size: 20px;
  }

  .hover-box .separator {
    width: 100%;
    height: 0px;
    border-top: 3px solid #000000;
  }

  .hover-box .lower-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 168px;
  }

  .hover-box .lower-container img {
    width: 290px;
  }
</style>
